import type {
  Announcement,
  AnnouncementCategoryInfo,
  AnnouncementComment,
  AnnouncementPlan,
  CommentTreeNode,
  ContentBlock,
  ReactionType,
} from "./types";

export const ANNOUNCEMENT_PLAN_ORDER: AnnouncementPlan[] = [
  "free",
  "artist",
  "manager",
  "premium",
  "ultra",
];

export const PLAN_COLOR_VARS: Record<AnnouncementPlan, string> = {
  free: "var(--cm-gray-800)",
  artist: "var(--cm-categorical-11)",
  manager: "var(--cm-categorical-7)",
  premium: "var(--cm-categorical-3)",
  ultra: "var(--cm-categorical-5)",
};

export const REACTION_ORDER: ReactionType[] = [
  "love",
  "thumbsup",
  "smile",
  "angry",
  "thumbsdown",
];

export const DEFAULT_CATEGORY_COLOR = "#6B7280";

export const REPLIES_COLLAPSE_THRESHOLD = 3;

export const EMPLOYEE_DISPLAY_NAME_SUFFIX = "@Chartmetric";

export const MAX_COMMENT_DISPLAY_NAME_LENGTH = 50;

export const COMMENT_IMAGE_MAX_FILES = 5;
export const COMMENT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const COMMENT_IMAGE_ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
export const COMMENT_IMAGE_ACCEPT = COMMENT_IMAGE_ACCEPTED_TYPES.join(",");

export function buildEmployeeDisplayName(name?: string | null): string {
  const trimmed = name?.trim();
  return trimmed
    ? `${trimmed} ${EMPLOYEE_DISPLAY_NAME_SUFFIX}`
    : EMPLOYEE_DISPLAY_NAME_SUFFIX;
}

export function formatAnnouncementDate(dateString: string | null): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function getTimeAgo(dateString: string | null): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return date.toLocaleDateString();
}

export function hasCommentBeenEdited(comment: AnnouncementComment): boolean {
  const createdAt = new Date(comment.created_at).getTime();
  const modifiedAt = new Date(comment.modified_at).getTime();
  if (Number.isNaN(createdAt) || Number.isNaN(modifiedAt)) return false;
  return modifiedAt - createdAt > 1000;
}

export function buildCommentTree(
  flatComments: AnnouncementComment[],
): CommentTreeNode[] {
  const nodeMap = new Map<number, CommentTreeNode>();
  const roots: CommentTreeNode[] = [];

  for (const comment of flatComments) {
    nodeMap.set(comment.id, { ...comment, replies: [] });
  }

  for (const comment of flatComments) {
    const node = nodeMap.get(comment.id);
    if (!node) continue;
    if (comment.parent_comment_id !== null) {
      const parent = nodeMap.get(comment.parent_comment_id);
      if (parent) {
        parent.replies.push(node);
      } else {
        roots.push(node);
      }
    } else {
      roots.push(node);
    }
  }

  return roots;
}

export function getTranslatedPost(
  announcement: Announcement,
  lang: string,
): { title: string; content: ContentBlock[] } {
  if (lang === "en" || !announcement.translations?.[lang]) {
    return { title: announcement.title, content: announcement.content };
  }
  const t = announcement.translations[lang];
  return {
    title: t.title || announcement.title,
    content: t.content?.length ? t.content : announcement.content,
  };
}

export function getTranslatedCategoryName(
  category: AnnouncementCategoryInfo,
  lang: string,
): string {
  if (lang === "en" || !category.translations?.[lang]) {
    return category.name;
  }
  return category.translations[lang]?.name || category.name;
}

export function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  const hasFullLength = normalized.length === 6;
  const value = hasFullLength ? normalized : DEFAULT_CATEGORY_COLOR.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function isChartmetricEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith("@chartmetric.com");
}
